import React from 'react';
import {Box, Button, Stack, Typography} from "@mui/material";
import {BrowserRouter, useLocation} from "react-router";
import {useSession} from "./firebase/UserProvider";
import MapEditorDemoPage from "./dev/mapDemo/MapEditorDemoPage";
import LoginContainer, {saveReturnUrl} from "./shared/LoginContainer";
import {RoutesAnalyst} from "./routes/routesAnalyst";
import {RoutesReviewer} from "./routes/routesReviewer";
import {RoutesPolicyMaker} from "./routes/routesPolicyMaker";
import {RoutesManager} from "./routes/routesManager";
import {RoutesCollector} from "./routes/routesCollector";
import {RoutesCollectionManager} from "./routes/routesCollectionManager";
import {RoutesBriefer} from "./routes/routesBriefer";
import {RoutesDisclosureOfficer} from "./routes/routesDisclosureOfficer";
import {RoutesRealUser} from "./routes/routesRealUser";
import CommunicationHub from './components/CommunicationHub';
import CollectionMaterialViewerHost from './features/collection/viewer/CollectionMaterialViewerHost';
import SpotlightSearch from './features/spotlight/SpotlightSearch';
import SourcePackageBuilder from './components/sourcePackage/SourcePackageBuilder';
import {logout} from "./firebase/auth";
import {useActivityTracking} from "./hooks/useActivityTracking";
import useAiWidgetStore from './stores/useAiWidgetStore';


// Roles that have a dedicated DASH UI route tree.
// 'admin' resolves to the analyst route tree at the root (full-featured: ICR, Products,
// Whiteboards, KB, Feeds) plus the Analytics page. Admins also retain access to /me/control.
// A user with both 'analyst' and 'admin' gets routed by their primary (analyst) role.
const KNOWN_ROLES = ['analyst','reviewer','policymaker','manager','collector','collection_manager','briefer','disclosure_officer','real_user','admin'];

const NoRoleView = ({user, roles}) => (
    <Stack height="100vh" alignItems="center" justifyContent="center" spacing={3} bgcolor="background.default">
        <Box textAlign="center" maxWidth={480} px={3}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Access not configured
            </Typography>
            <Typography color="text.secondary" variant="body1">
                Your account (<strong>{user?.email}</strong>) has roles&nbsp;
                <code style={{background:'#f5f5f5', padding:'2px 6px', borderRadius:4}}>{roles?.join(', ') || 'unknown'}</code>
                &nbsp;which do not have a DASH interface assigned yet.
            </Typography>
            <Typography color="text.secondary" variant="body2" mt={1}>
                Contact your administrator to request the correct role for your account.
            </Typography>
            <Button
                variant="outlined"
                sx={{mt: 3, textTransform: 'none'}}
                onClick={async () => {
                    saveReturnUrl();
                    await logout();
                    window.location.href = '/?logout';
                }}
            >
                Sign out
            </Button>
        </Box>
    </Stack>
);

// Mounts client-side user activity tracking (page views, session heartbeat, session end).
// Rendered inside the Router and only for authenticated, routed users.
const ActivityTracker = () => {
    useActivityTracking();
    return null;
};

const App = () => {
    const {user, role, roles} = useSession();
    const open = useAiWidgetStore((s) => s.open);
    const panelWidth = useAiWidgetStore((s) => s.panelWidth);
    const location = useLocation();

    // dev-only demo harness (src/dev/mapDemo) — intentionally ahead of the auth gate below so
    // it's reachable without a seeded backend account; not part of any production route tree.
    if (location.pathname.startsWith('/dev/map-demo')) {
        return <MapEditorDemoPage/>;
    }

    if (!user || !role) {
        return <LoginContainer/>;
    }

    // Find the first role in the user's roles list that has a known UI route.
    // This lets a user with ['analyst', 'admin'] get the analyst UI.
    const allRoles = roles || [role];
    const routeRole = allRoles.find(r => KNOWN_ROLES.includes(r));

    if (!routeRole) {
        return <NoRoleView user={user} roles={allRoles}/>;
    }

    return (
        <>
            <Stack height={"100vh"}
                   bgcolor={'background.default'}
                   sx={{
                       width: open ? `calc(100vw - ${panelWidth}px)` : '100vw',
                       maxWidth: open ? `calc(100vw - ${panelWidth}px)` : '100vw',
                       transition: 'width 220ms ease, max-width 220ms ease',
                       overflow: 'hidden',
                   }}
            >
                <ActivityTracker/>
                {(routeRole === 'analyst' || routeRole === 'admin') && <RoutesAnalyst/>}
                {routeRole === 'reviewer' && <RoutesReviewer/>}
                {routeRole === 'policymaker' && <RoutesPolicyMaker/>}
                {routeRole === 'manager' && <RoutesManager/>}
                {routeRole === 'collector' && <RoutesCollector/>}
                {routeRole === 'collection_manager' && <RoutesCollectionManager/>}
                {routeRole === 'briefer' && <RoutesBriefer/>}
                {routeRole === 'disclosure_officer' && <RoutesDisclosureOfficer/>}
                {routeRole === 'real_user' && <RoutesRealUser/>}
            </Stack>
            <CommunicationHub/>
            <SpotlightSearch/>
            {/* Corpus / file / report viewer overlay — globally available so spotlight can open hits from any page */}
            <SourcePackageBuilder/>
        </>
    );
};

/**
 * Top-level shell rendered once for the lifetime of the app.
 *
 * Defined in its own module (not index.js) so React Fast Refresh can track
 * stable component identities for App and CommunicationHub, preventing
 * spurious unmount/remount cycles on navigation.
 *
 * CommunicationHub is rendered inside App (after the auth gate) so it is
 * never mounted on the login page or for unauthenticated users.
 */
const Shell = () => (
    <BrowserRouter>
        <App/>
        {/* CM viewer modal (contract C2) — reachable from any page via cmView* URL params */}
        <CollectionMaterialViewerHost/>
    </BrowserRouter>
);

export default Shell;
